import {Participant, Role, ServiceCenter, Traininggroup} from "../../../../../types/domain";
import {Alert, Button, Checkbox, Input, Modal, ScrollArea, Select, TextInput} from "@mantine/core";
import React, {useEffect, useState} from "react";
import ServicecenterService from "../../../../../services/servicecenter.service";
import ParticipantService from "../../../../../services/participant.service";
import TraininggroupService from "../../../../../services/traininggroup.service";
import * as Sentry from "@sentry/react";

type GroupModalProps = {
    opened: boolean;
    close: () => void;
    title?: string;
    group: Traininggroup | null;
    userRole: Role | null;
    userServiceCenters: Array<ServiceCenter>;
};

export function GroupModal({opened, close, title,
                               group, userRole, userServiceCenters}: GroupModalProps) {
    const [serviceCenters, setServiceCenters] = useState<ServiceCenter[]>([]);
    const [serviceCenter, setServiceCenter] = useState("");
    const [groupNames, setGroupNames] = useState<string[]>([]);
    const [groupNameState, setGroupNameState] = useState("");

    const [participants, setParticipants] = useState<Participant[]>([]);
    const [selectedParticipants, setSelectedParticipants] = useState<Participant[]>([]);

    const [showInputAlert, setShowInputAlert] = useState({
        show: false,
        message: ""
    });
    const [showAlert, setShowAlert] = useState(false);

    // Disable changing service center if any participant is selected
    const serviceCenterDisabled = selectedParticipants.length > 0;

    // Load service centers on open or userRole/userServiceCenters change
    useEffect(() => {
        async function loadServiceCenters() {
            try {
                if (userRole?.name === "Admin") {
                    const response = await ServicecenterService.getAll();
                    setServiceCenters(response.data);
                } else if (userRole?.name === "DSS Leiter*in") {
                    setServiceCenters(userServiceCenters);
                }
            } catch (e) {
                console.error("Error loading service centers:", e);
            }
        }

        if (opened) {
            loadServiceCenters();
        }
    }, [opened, userRole, userServiceCenters]);

    // Reset form or load group data when modal opens or group changes
    useEffect(() => {
        if (opened) {
            //Load all groupNames
            const loadGroups = async() => {
                let groups = (await TraininggroupService.getAll()).data.map(g => g.name);
                if (group){
                    groups = groups.filter(g => g !== group.name)  //remove current group name from all groupNames, when editing group
                }
                setGroupNames(groups);
            }

            loadGroups();

            // Set remaining fields
            if (group) {
                // Edit mode: preload fields with group data
                setServiceCenter(group.serviceCenter?.id.toString() ?? "");
                setGroupNameState(group.name);

                const loadParticipantsForGroup = async () => {
                    if (group.serviceCenter) {
                        try {
                            // Get participants that are not part of a group
                            const scParticipants = await ParticipantService.getByServiceCenterName(group.serviceCenter.name);
                            let allParticipants: Participant[] = scParticipants.data.filter(p => p.active);

                            const groupParticipants = group.participants ?? [];
                            groupParticipants.forEach((gp) => {
                                if (!allParticipants.some((p) => p.id === gp.id && gp.active)) {
                                    allParticipants.push(gp);
                                }
                            });

                            setParticipants(allParticipants);
                            setSelectedParticipants(groupParticipants);
                        } catch (e) {
                            console.error("Error loading participants for group:", e);
                        }
                    } else {
                        setParticipants([]);
                        setSelectedParticipants([]);
                    }
                };

                loadParticipantsForGroup();
            } else {
                // Create mode: reset all fields
                setServiceCenter("");
                setGroupNameState("");
                setParticipants([]);
                setSelectedParticipants([]);
            }

            // Reset alerts on open
            setShowInputAlert({... showInputAlert, show: false});
            setShowAlert(false);
        }
    }, [opened, group]);

    // Load participants when a service center is selected in create mode
    useEffect(() => {
        if (!group && serviceCenter) {
            const loadParticipantsForServiceCenter = async () => {
                try {
                    const sc = serviceCenters.find((s) => s.id.toString() === serviceCenter);
                    if (sc && sc.name) {
                        const scParticipants = await ParticipantService.getByServiceCenterName(sc.name);
                        let filteredParticipants: Participant[] = scParticipants.data.filter(p => p.active);
                        setParticipants(filteredParticipants);
                        setSelectedParticipants([]); // No pre-selection in create mode
                    } else {
                        setParticipants([]);
                        setSelectedParticipants([]);
                    }
                } catch (e) {
                    console.error("Error loading participants:", e);
                }
            };

            loadParticipantsForServiceCenter();
        } else if (!group && !serviceCenter) {
            // Clear participants if no service center selected in create mode
            setParticipants([]);
            setSelectedParticipants([]);
        }
    }, [serviceCenter, group, serviceCenters]);


    // Handle checkbox toggle for participant selection
    function handleCheckboxChange(participant: Participant) {
        setSelectedParticipants((prev) => {
            const exists = prev.some(p => p.id === participant.id);
            if (exists) {
                //Remove
                return prev.filter(p => p.id !== participant.id);
            } else {
                //Add
                return [...prev, participant];
            }
        });
    }

    async function updateGroup() {
        if (!groupNameState || !serviceCenter || !group) {
            setShowInputAlert({show: true, message: "Bitte füllen Sie alle Felder aus!"});
            return;
        }

        // Check if group name already exists
        if(groupNames.includes(groupNameState.trimEnd())){
            setShowInputAlert({show: true, message: "Diese Gruppe existiert bereits!"});
            return;
        }

        setShowInputAlert({... showInputAlert, show: false});

        const updatedGroup: Traininggroup = {
            id: group!.id,
            name: groupNameState.trimEnd(),
            participants: selectedParticipants,
            serviceCenter: serviceCenters.find(sc => sc.id.toString() === serviceCenter),
            active: true
        };

        //update group
        const result = await TraininggroupService.update(updatedGroup, updatedGroup.id)
            .catch((e: Error) => {
                Sentry.captureException(e, {
                    extra: {
                        request: {
                            updatedGroup: JSON.parse(JSON.stringify(updatedGroup)),
                        },
                    },
                });
                setShowAlert(true);
                return;
            });

        const oldParticipants = group?.participants ?? [];
        const addedParticipants = selectedParticipants.filter(
            sp => !oldParticipants.some(op => op.id === sp.id)
        );
        const removedParticipants = oldParticipants.filter(
            op => !selectedParticipants.some(sp => sp.id === op.id)
        );

        //Set Group for added participants
        for (const p of addedParticipants) {
            p.group = result!.data;
            p.serviceCenter = null;
            await ParticipantService.update(p, p.id)
                .catch((e: Error) => {
                    Sentry.captureException(e);
                    setShowAlert(true);
                    return;
                });
        }

        // Remove group and set servicecenter for removed participants
        for (const p of removedParticipants) {
            p.group = null;
            p.serviceCenter = group?.serviceCenter
            await ParticipantService.update(p, p.id)
                .catch((e: Error) => {
                    Sentry.captureException(e);
                    setShowAlert(true);
                    return
                });
        }

        close();
    }


    async function addGroup() {
        if (!groupNameState || !serviceCenter) {
            setShowInputAlert({show: true, message: "Bitte füllen Sie alle Felder aus!"});
            return;
        }

        // Check if group name already exists
        if(groupNames.includes(groupNameState.trimEnd())){
            setShowInputAlert({show: true, message: "Diese Gruppe existiert bereits!"});
            return;
        }

        setShowInputAlert({... showInputAlert, show: false});

        const g: Traininggroup = {
            id: 0,
            name: groupNameState.trimEnd(),
            participants: selectedParticipants,
            serviceCenter: serviceCenters.find(sc => sc.id.toString() === serviceCenter),
            active: true
        };

        let result = await TraininggroupService.create(g) //Insert group
            .catch((e: Error) => {
                Sentry.captureException(e, {
                    extra: {
                        request: {
                            group: JSON.parse(JSON.stringify(g)),
                        },
                    },
                });
                setShowAlert(true);
                return;
            });

        g.participants.forEach(p => {
            p.group = result!.data;
            p.serviceCenter = null;
            ParticipantService.update(p, p.id) //Set Group at Participants
                .catch((e: Error) => {
                    Sentry.captureException(e);
                    setShowAlert(true);
                    return;
                });
        });

        close();

    }

    /**
     * Helper function to get the right label for the participants,
     * depending on whether the birthDate is set or not
     * @param p Participant entity
     */
    function getParticipantLabel(p: Participant) {
        var label = `${p.firstname} ${p.lastname}`
        if (p.birthDate){
            // Change format to valid frontend format
            const dateTokens = p.birthDate?.split("-")
            label += `, ${dateTokens[2]}.${dateTokens[1]}.${dateTokens[0]}`
        }
        return label;
    }

    return (
        <Modal opened={opened} onClose={close} centered title={title}>
            <Input.Wrapper>
                <TextInput
                    label="Gruppenname"
                    mb="sm"
                    value={groupNameState}
                    withAsterisk
                    onChange={(e) => setGroupNameState(e.currentTarget.value)}
                />
                <Select
                    label="Servicestelle"
                    mb="sm"
                    withinPortal
                    data={serviceCenters.map((sc) => ({
                        value: sc.id.toString(),
                        label: sc.name
                    }))}
                    value={serviceCenter}
                    withAsterisk
                    disabled={serviceCenterDisabled}
                    onChange={(value) => {
                        if (value) {
                            setServiceCenter(value);
                        }
                    }}
                    placeholder="Servicestelle auswählen"
                />
            </Input.Wrapper>

            {/* Display participant list with checkboxes */}
            {participants.length > 0 && (
                <ScrollArea.Autosize mah={200} mx={"auto"}>
                    {participants.map((p) => (
                        <Checkbox
                            key={p.id}
                            label={getParticipantLabel(p)}
                            checked={selectedParticipants.some(sp => sp.id === p.id)}
                            onChange={() => handleCheckboxChange(p)}
                            mb="xs"
                        />
                    ))}
                </ScrollArea.Autosize>
            )}

            {/* Validation Alerts */}
            {showInputAlert.show && (
                <Alert
                    w="100%"
                    title={group ? "Aktualisieren fehlgeschlagen" : "Hinzufügen fehlgeschlagen"}
                    ta="left"
                    color="red"
                    mt="10px"
                >
                    {showInputAlert.message}
                </Alert>
            )}
            {showAlert && (
                <Alert
                    w="100%"
                    title={group ? "Aktualisieren fehlgeschlagen" : "Hinzufügen fehlgeschlagen"}
                    ta="left"
                    color="red"
                    mt="10px"
                >
                    Leider ist ein Fehler während des {group ? "aktualisieren" : "einfügen"} aufgetreten.
                    Versuchen Sie es noch einmal oder kontaktieren Sie die Administratoren.
                </Alert>
            )}

            <div className="buttonRow">
                <Button onClick={group ? updateGroup : addGroup} mb="sm">
                    {group ? "Aktualisieren" : "Hinzufügen"}
                </Button>
            </div>
        </Modal>
    );
}